import type { Identificable, LabelledEntity } from 'shared/types/commonView';
import type { CreatableUserRole, UserRole } from 'shared/types/userRole';
import type { PersonalData } from './entity';
import {
  AccessLevelOnDropdownView,
  type AccessLevelOnIconView,
  type UserAccess,
} from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import type { ProcessType } from 'shared/domain/process/types/model';
import type { SiteModel } from 'shared/domain/site/types/model';

interface BaseUserOnView {
  email: string;
  label: string;
  phone: string;
  processes: LabelledEntity[];
  sites: LabelledEntity[];
}

export type UserOnView = LabelledEntity &
  BaseUserOnView & { role: UserRoleOnView };

export type UserOnViewWithIssueAccessLevel = UserOnView & {
  accessLevel: AccessLevelOnIconView;
};

/** A project user as the issue views show it, with their access level; `null` for an unknown user. */
export type GetUserOnViewWithAccessLevelOnIconViewById = (
  userId: string,
  userAccesses: UserAccess[],
  sites: SiteModel[],
  processes: ProcessType[]
) => UserOnViewWithIssueAccessLevel | null;

/** Narrows when the object was built with issue access metadata (e.g. userModelToUserOnViewWithAccessLevel). */
export function isUserOnViewWithIssueAccessLevel(
  user: UserOnView
): user is UserOnViewWithIssueAccessLevel {
  return (
    typeof user === 'object' &&
    user !== null &&
    'accessLevel' in user &&
    user.accessLevel !== undefined &&
    user.accessLevel !== null
  );
}

export interface UserRoleOnView { label: string; _id: UserRole }
export interface CreatableUserRoleOnView {
  label: string;
  _id: CreatableUserRole;
}

export interface UserCreateOnView {
  email: string;
  role: CreatableUserRoleOnView;
  processes: LabelledEntity[];
  sites: LabelledEntity[];
}

export type UserEditOnView = Identificable &
  BaseUserOnView & {
    role: CreatableUserRoleOnView;
    personalData: PersonalData[];
    canBePersonalDataModified?: boolean;
  };

export type UserLabelledWithEmail = LabelledEntity & { email: string };
export type UserLabelledWithEmailAndTranslatedRole =
  UserLabelledWithEmail & { role: string };

export type TargetUserOnAccessChangeView = SubmitterOnAccessChangeView & {
  label: string;
  email: string;
};

export interface SubmitterOnAccessChangeView {
  _id: string;
  role: UserRole;
  sites: string[];
  processes: string[];
}
